import React, { useState, useCallback } from 'react';
import { Search, Globe, CheckCircle } from 'lucide-react';

export interface URLInputProps {
  onURLSubmit: (url: string) => void;
  isLoading?: boolean;
  error?: string | null;
}

const URLInput: React.FC<URLInputProps> = ({ onURLSubmit, isLoading = false, error = null }) => {
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);

  const validateURL = useCallback((inputUrl: string): boolean => {
    if (!inputUrl.trim()) {
      setUrlError('Please enter a website URL');
      return false;
    }

    // Add protocol if missing
    let processedUrl = inputUrl.trim();
    if (!processedUrl.match(/^https?:\/\//)) {
      processedUrl = `https://${processedUrl}`;
    }

    try {
      const urlObj = new URL(processedUrl);
      
      // Basic validation
      if (!urlObj.hostname || urlObj.hostname.length < 3) {
        setUrlError('Please enter a valid website URL (e.g., example.com)');
        return false;
      }

      // Check for valid domain format
      const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!domainPattern.test(urlObj.hostname)) {
        setUrlError('Please enter a valid domain name');
        return false;
      }

      setUrlError(null);
      setUrl(processedUrl);
      return true;
    } catch {
      setUrlError('Please enter a valid website URL (e.g., https://example.com)');
      return false;
    }
  }, []);

  const handleURLChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setUrl(inputValue);
    
    // Real-time validation
    if (inputValue.trim()) {
      const isValid = validateURL(inputValue);
      setIsValidated(isValid);
    } else {
      setUrlError(null);
      setIsValidated(false);
    }
  }, [validateURL]);

  const handleSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    
    if (validateURL(url)) {
      // Add protocol if missing for the final URL
      let processedUrl = url.trim();
      if (!processedUrl.match(/^https?:\/\//)) {
        processedUrl = `https://${processedUrl}`;
      }
      onURLSubmit(processedUrl);
    }
  }, [url, validateURL, onURLSubmit]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isLoading && isValidated) {
      handleSubmit(event);
    }
  }, [handleSubmit, isLoading, isValidated]);

  return (
    <div style={{ width: '100%', maxWidth: '600px' }}>
      <form onSubmit={handleSubmit}>
        <div style={{ position: 'relative', marginBottom: '16px' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Enter website URL (e.g., shopify.com or https://amazon.com)"
              value={url}
              onChange={handleURLChange}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              style={{
                width: '100%',
                height: '56px',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                fontSize: '16px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                border: `1px solid ${(urlError || error) ? '#EF4444' : '#E5E7EB'}`, // Tabby border colors
                padding: '16px 16px 16px 48px', // Left padding for icon
                color: '#1A202C', // Tabby primary text
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                if (!(urlError || error)) {
                  e.target.style.border = '2px solid #3ECC71'; // Green focus border
                  e.target.style.padding = '15px 15px 15px 47px'; // Adjust for thicker border
                }
              }}
              onBlur={(e) => {
                e.target.style.border = `1px solid ${(urlError || error) ? '#EF4444' : '#E5E7EB'}`;
                e.target.style.padding = '16px 16px 16px 48px';
              }}
            />
            
            {/* Globe Icon */}
            <Globe 
              size={20} 
              style={{ 
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: isValidated ? '#3ECC71' : '#9CA3AF'
              }} 
            />
            
            {/* Success checkmark */}
            {isValidated && !isLoading && (
              <CheckCircle 
                size={20} 
                style={{ 
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#3ECC71'
                }} 
              />
            )}
            
            {/* Loading spinner */}
            {isLoading && (
              <div style={{ 
                position: 'absolute', 
                right: '16px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                border: '2px solid #E5E7EB',
                borderTop: '2px solid #3ECC71',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}>
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
              </div>
            )}
          </div>
          
          {/* Error/Helper Text */}
          {(urlError || error) && (
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '14px',
              color: '#EF4444', // Tabby error color
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}>
              {urlError || error}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={!isValidated || isLoading}
          style={{
            width: '100%',
            height: '56px',
            backgroundColor: (!isValidated || isLoading) ? '#E5E7EB' : '#000000', // Pure black primary button
            color: (!isValidated || isLoading) ? '#9CA3AF' : '#FFFFFF',
            fontSize: '16px',
            fontWeight: 600,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            borderRadius: '8px',
            border: 'none',
            cursor: (!isValidated || isLoading) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {isLoading ? (
            <>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid #FFFFFF',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <span>Checking website...</span>
            </>
          ) : (
            <>
              <Search size={20} />
              Check Website
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default URLInput;
